/**
 * useGuestGuard - Hook for gating actions behind authentication.
 * Shows a contextual sign-up dialog when a guest tries a protected action.
 * Returns { isGuest, requireAuth } where requireAuth returns false if blocked (guest).
 */

import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useDialogStore } from '../stores/dialogStore';
import { useMenuStore } from '../stores/menuStore';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics';
import { GUEST_PROMPT_COPY } from './useGuestGuard.constants';
import { buildPublicSignInHref } from '../../signIn/helpers/postAuthRedirect.helper';

interface UseGuestGuardReturn {
  /** Whether the current user is browsing as a guest. */
  isGuest: boolean;
  /**
   * Gate an action behind authentication.
   * Guests: shows the sign-up dialog (async); returns false immediately so the caller skips the action.
   * Signed-in: returns true (caller should proceed).
   *
   * @param action - Human-readable action description (e.g. 'invite friends')
   * @param returnUrl - Optional URL to redirect to after sign-up completes
   */
  requireAuth: (action: string, returnUrl?: string) => boolean;
}

export function useGuestGuard(): UseGuestGuardReturn {
  const { isAnonymous } = useSupabaseAuth();
  const showDialog = useDialogStore((s) => s.showDialog);
  const router = useRouter();
  const { track } = useAnalytics();

  const isGuest = isAnonymous;

  const requireAuth = useCallback(
    (action: string, returnUrl?: string): boolean => {
      if (!isGuest) return true;

      track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_SHOWN, { action });

      void showDialog({
        title: GUEST_PROMPT_COPY.TITLE,
        message: `${GUEST_PROMPT_COPY.MESSAGE_PREFIX}${action}`,
        confirmText: GUEST_PROMPT_COPY.CONFIRM,
        cancelText: GUEST_PROMPT_COPY.CANCEL,
      }).then((confirmed) => {
        if (confirmed) {
          track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_ACCEPTED, { action });
          useMenuStore.getState().setMenuVisible(false);
          const href = buildPublicSignInHref({ signup: true, returnUrl });
          router.push(href as `/${string}`);
        } else {
          track(ANALYTICS_EVENTS.GUEST_SIGNUP_PROMPT_DISMISSED, { action });
        }
      });

      return false;
    },
    [isGuest, showDialog, router, track],
  );

  return { isGuest, requireAuth };
}
